import { handleScreenshot } from "../utils/fileUpload";
import { addPosition, pixelToGridPosition } from "../utils/gridPosition";
import { useCallback, useEffect, useRef } from "react";
import { AnyBlock } from "../types";
import { useScrollRef } from "../jotai/atoms";
import useSound from "use-sound";
import { Presence } from "../types";
import { useUpdateMyPresence } from "@liveblocks/react";
import { useSendAnalyticsEvent } from "../analytics/events";

export function useStampModeSetup() {
  const updateMyPresence = useUpdateMyPresence();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // escape hatch in case stuck in stamp mode
      if (e.key === "Escape") {
        updateMyPresence({ stampMode: "inactive" });
      }
      if (document.activeElement && document.activeElement.nodeName !== "BODY")
        return;
      if (e.key.toLowerCase() === "s" && !e.repeat) {
        updateMyPresence({ stampMode: "active" });
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "s") {
        updateMyPresence({ stampMode: "inactive" });
      }
    };
    // Deactivate when you leave browser window
    // Prevents you from being stuck in stamp mode if you release 's' in another window
    const handleWindowLeave = () => {
      updateMyPresence({ stampMode: "inactive" });
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    document.addEventListener("mouseleave", handleWindowLeave);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      document.removeEventListener("mouseleave", handleWindowLeave);
    };
  }, [updateMyPresence]);
}

export function useStampSound(stampMode: Presence["stampMode"]) {
  const [playStamp] = useSound("/assets/sounds/stamp.mp3", {
    volume: 0.2,
  });
  useEffect(() => {
    if (stampMode === "pressed") {
      playStamp();
    }
  }, [stampMode, playStamp]);
}

function useMakeStamp(
  upsertBlock: (block: Partial<AnyBlock>) => void,
  video: HTMLVideoElement | null
) {
  const scroll = useScrollRef();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  useEffect(() => {
    canvasRef.current = document.createElement("canvas");
  }, []);

  return useCallback(() => {
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    sendAnalyticsEvent({ name: "faceStamped" });

    const videoRect = video.getBoundingClientRect();
    const { left, top } = videoRect;

    // Double resolution
    const width = videoRect.width * 2;
    const height = videoRect.height * 2;

    // Cover
    const aspectRatio = video.videoHeight / video.videoWidth;
    let adjustedWidth = width;
    let adjustedHeight = height;
    if (aspectRatio > 1) {
      adjustedHeight = width * aspectRatio;
    } else {
      canvas.width = height / aspectRatio;
      canvas.height = height;
      adjustedWidth = height / aspectRatio;
    }

    canvas.width = width;
    canvas.height = height;

    // draw the video at that frame
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new TypeError("failed to create context");
    }
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.beginPath();
    ctx.arc(
      canvas.width / 2,
      canvas.height / 2,
      Math.min(canvas.height, canvas.width) / 2,
      0,
      Math.PI * 2
    );
    ctx.clip();

    ctx.drawImage(
      video,
      -(adjustedWidth - canvas.width) / 2,
      (adjustedHeight - canvas.height) / 2,
      adjustedWidth,
      adjustedHeight
    );

    canvas.toBlob(
      (blob) => {
        if (!scroll?.current) return;
        if (blob) {
          handleScreenshot(
            blob,
            upsertBlock,
            pixelToGridPosition(
              addPosition(
                {
                  x: left,
                  y: top,
                },
                scroll.current
              )
            ),
            { width: width / 2, height: height / 2 },
            false
          );
        }
      },
      // use png for transparency
      "image/png"
    );
  }, [upsertBlock, video, scroll, sendAnalyticsEvent]);
}

export function StampMaker({
  upsertBlock,
  videoRef,
}: {
  upsertBlock: (block: Partial<AnyBlock>) => void;
  videoRef: React.RefObject<HTMLVideoElement>;
}) {
  const updateMyPresence = useUpdateMyPresence();
  const makeStamp = useMakeStamp(upsertBlock, videoRef.current);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    updateMyPresence({ stampMode: "pressed" });
  };
  const handleMouseUp = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    updateMyPresence({ stampMode: "active" });
    makeStamp();
  };

  useEffect(() => {
    const handleSpaceDown = (e: KeyboardEvent) => {
      if (e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        if (!e.repeat) {
          updateMyPresence({ stampMode: "pressed" });
        }
      }
    };
    const handleSpaceUp = (e: KeyboardEvent) => {
      if (e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        updateMyPresence({ stampMode: "active" });
        makeStamp();
      }
    };
    window.addEventListener("keydown", handleSpaceDown);
    window.addEventListener("keyup", handleSpaceUp);
    return () => {
      window.removeEventListener("keydown", handleSpaceDown);
      window.removeEventListener("keyup", handleSpaceUp);
    };
  }, [updateMyPresence, makeStamp]);

  // div provides target for pointer events and also makes sure cursor
  // stays properly hidden
  const targetSize = 256;
  return (
    <div
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      style={{
        position: "absolute",
        top: -targetSize / 2,
        left: -targetSize / 2,
        width: targetSize,
        height: targetSize,
        pointerEvents: "auto",
        cursor: "none",
        background: "transparent",
      }}
    ></div>
  );
}
